import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../api';
import { useAuth } from '../context/auth';
import { usePlayback } from '../context/playback';
import { LoadingText } from '../components/Style';
import {
  MusicContainer,
  MusicCard,
  TabBar,
  Tab,
  CommunitySection,
  EmptyText,
  SectionHeading,
  BackChevron,
} from '../components/MusicPageStyles';
import { ReactComponent as PlayIcon } from '../img/play.svg';
import { ReactComponent as PauseIcon } from '../img/pause.svg';
import ShareModal from '../components/ShareModal';
import CompactPostCard from '../components/CompactPostCard';
import { trackEvent } from '../analytics';

var HeaderBanner = styled.div`
  background: black;
  color: white;
  padding: 0.6em 1em;
`;

var ArtistName = styled.h1`
  font-weight: 900;
  font-size: x-large;
  margin: 0;
`;

var ArtistHero = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  display: block;
`;

var RelatedGrid = styled.div`
  width: 100%;
  max-width: 400px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 0 0.5rem;
`;

var RelatedCard = styled(Link)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
`;

var RelatedBanner = styled.div`
  background: black;
  color: white;
  padding: 0.35em 0.5em;
`;

var RelatedName = styled.div`
  font-size: 0.7rem;
  font-weight: 900;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

var RelatedArtwork = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  display: block;
`;

// --- Album card styles ---

var AlbumGrid = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
`;

var AlbumCard = styled(Link)`
  width: 100%;
  max-width: 400px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  overflow: hidden;
  text-decoration: none;
  color: white;
  display: flex;
  flex-direction: column;
  background: white;
`;

var AlbumBanner = styled.div`
  background: black;
  color: white;
  padding: 0.6em 1em;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

var AlbumArtwork = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  display: block;
`;

var AlbumText = styled.div`
  flex: 1;
  min-width: 0;
`;

var AlbumTitle = styled.div`
  font-weight: 300;
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

var AlbumMeta = styled.div`
  font-size: 0.75rem;
  opacity: 0.6;
`;

var AlbumPlayButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1.5px solid white;
  background: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

// --- Song row styles ---

var SongRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
  gap: 0.6rem;
  width: 100%;
`;

var SongArtwork = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

var SongText = styled(Link)`
  flex: 1;
  min-width: 0;
  text-decoration: none;
  color: inherit;
`;

var SongTitle = styled.div`
  font-weight: 700;
  font-size: 0.85rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

var SongMeta = styled.div`
  font-size: 0.75rem;
  opacity: 0.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

var SongPlayButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #ccc;
  background: none;
  color: black;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #f0f0f0;
  }
`;

var SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 400px;
`;

var FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  width: 100%;
  max-width: 400px;
`;

var FilterChip = styled.button`
  font-size: 0.7rem;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  border: 1px solid ${function (p) {
      return p.$active ? '#000' : '#ccc';
    }};
  background: ${function (p) {
    return p.$active ? '#000' : 'white';
  }};
  color: ${function (p) {
    return p.$active ? 'white' : '#666';
  }};
  cursor: pointer;
  font-family: inherit;
  font-weight: ${function (p) {
    return p.$active ? '700' : '400';
  }};

  &:hover {
    border-color: #999;
  }
`;

var SortToggle = styled.button`
  background: none;
  border: none;
  font-size: 0.75rem;
  color: #999;
  cursor: pointer;
  padding: 0.25rem 0;
  font-weight: ${function (p) {
    return p.$active ? '700' : '400';
  }};
  color: ${function (p) {
    return p.$active ? '#000' : '#999';
  }};
`;

var SortGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

var LoadMoreButton = styled.button`
  display: block;
  margin: 0.75rem auto 0;
  background: white;
  color: black;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.8rem;
  padding: 0.5rem 1.25rem;
  cursor: pointer;

  &:hover {
    background: #f0f0f0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export default function ArtistPage() {
  var { artistName } = useParams();
  var decodedArtist = decodeURIComponent(artistName || '');
  var navigate = useNavigate();
  var location = useLocation();
  var [searchParams] = useSearchParams();
  var urlAppleId = searchParams.get('appleId');
  var playback = usePlayback();

  var _appleId = useState(urlAppleId);
  var appleId = _appleId[0];
  var setAppleId = _appleId[1];

  var _loading = useState(true);
  var loading = _loading[0];
  var setLoading = _loading[1];

  var _error = useState(null);
  var error = _error[0];
  var setError = _error[1];

  var _data = useState(null);
  var data = _data[0];
  var setData = _data[1];

  var _showShare = useState(false);
  var showShare = _showShare[0];
  var setShowShare = _showShare[1];

  var _catalog = useState(null);
  var catalog = _catalog[0];
  var setCatalog = _catalog[1];

  var _artistDetail = useState(null);
  var artistDetail = _artistDetail[0];
  var setArtistDetail = _artistDetail[1];

  var _communityPosts = useState([]);
  var communityPosts = _communityPosts[0];
  var setCommunityPosts = _communityPosts[1];

  var _communityLoading = useState(false);
  var communityLoading = _communityLoading[0];
  var setCommunityLoading = _communityLoading[1];

  var _communityCursor = useState(null);
  var communityCursor = _communityCursor[0];
  var setCommunityCursor = _communityCursor[1];

  var _communityHasMore = useState(false);
  var communityHasMore = _communityHasMore[0];
  var setCommunityHasMore = _communityHasMore[1];

  var _albumSort = useState(location.state?.albumSort || 'date-desc');
  var albumSort = _albumSort[0];
  var setAlbumSort = _albumSort[1];

  var _activeTab = useState(location.state?.activeTab || null);
  var activeTab = _activeTab[0];
  var setActiveTab = _activeTab[1];

  var _decadeFilter = useState(location.state?.decadeFilter || null);
  var decadeFilter = _decadeFilter[0];
  var setDecadeFilter = _decadeFilter[1];

  // Restore navigation state when returning from album/song pages
  useEffect(
    function () {
      if (location.state?.albumSort) setAlbumSort(location.state.albumSort);
      if (location.state?.decadeFilter) setDecadeFilter(location.state.decadeFilter);
      if (location.state?.activeTab) setActiveTab(location.state.activeTab);
    },
    [location.state]
  ); // eslint-disable-line react-hooks/exhaustive-deps

  var authContext = useAuth();
  var isLoggedIn = authContext.authTokens?.token;

  useEffect(
    function () {
      if (data) {
        document.title = data.artist + ' | HeavyRotation';
      }
    },
    [data]
  );

  useEffect(
    function () {
      if (!decodedArtist) {
        setError(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      trackEvent('artist_page_viewed', { artist: decodedArtist });

      // Fetch DB data and Apple Music catalog in parallel
      var dbPromise = api.get('/api/media/by-artist', { params: { artist: decodedArtist } });
      var appleParams = { artist: decodedArtist };
      if (urlAppleId) appleParams.appleId = urlAppleId;
      var catalogPromise = api
        .get('/api/music/artist-albums', { params: appleParams })
        .catch(function () {
          return { data: { albums: [] } };
        });
      var detailPromise = api
        .get('/api/music/artist-detail', { params: appleParams })
        .catch(function () {
          return { data: { artworkURL: null, relatedArtists: [] } };
        });

      Promise.all([dbPromise, catalogPromise, detailPromise])
        .then(function (results) {
          setData(results[0].data);
          setCatalog(results[1].data.albums || []);
          setArtistDetail(results[2].data);
          // Capture appleId from response
          var resolvedId = results[2].data.appleId || results[1].data.appleId || urlAppleId;
          if (resolvedId) setAppleId(resolvedId);
          setLoading(false);
        })
        .catch(function () {
          setError(true);
          setLoading(false);
        });
    },
    [decodedArtist]
  );

  // Fetch community posts when artist data loads
  useEffect(
    function () {
      if (!data) return;
      setCommunityLoading(true);
      setCommunityPosts([]);
      setCommunityCursor(null);
      setCommunityHasMore(false);

      api
        .get('/api/media/by-artist/posts', {
          params: { artist: data.artist, limit: 10 },
        })
        .then(function (response) {
          var result = response.data;
          var posts = result.posts || [];
          setCommunityPosts(posts);
          setCommunityCursor(result.nextCursor || null);
          setCommunityHasMore(!!result.nextCursor);
          setCommunityLoading(false);
          if (
            activeTab === null ||
            (activeTab !== 'posts' &&
              activeTab !== 'albums' &&
              activeTab !== 'mentions' &&
              activeTab !== 'related')
          ) {
            setActiveTab(posts.length > 0 ? 'posts' : 'albums');
          }
        })
        .catch(function () {
          setCommunityLoading(false);
        });
    },
    [data]
  );

  function loadMoreCommunityPosts() {
    if (!communityCursor || communityLoading) return;
    setCommunityLoading(true);

    api
      .get('/api/media/by-artist/posts', {
        params: { artist: data.artist, limit: 10, before: communityCursor },
      })
      .then(function (response) {
        var result = response.data;
        setCommunityPosts(function (prev) {
          return prev.concat(result.posts || []);
        });
        setCommunityCursor(result.nextCursor || null);
        setCommunityHasMore(!!result.nextCursor);
        setCommunityLoading(false);
      })
      .catch(function () {
        setCommunityLoading(false);
      });
  }

  function handleAlbumPlay(album, e) {
    e.preventDefault();

    if (!album.upc) return;

    // If this album is already playing, toggle pause/play
    if (playback.currentTrack && playback.currentTrack.artist === data.artist) {
      // Check if any track from this album is currently playing
      var isThisAlbum = playback.currentTrack.artworkURL === album.artworkURL;
      if (isThisAlbum) {
        playback.togglePlayPause();
        return;
      }
    }

    api
      .get('/api/music/album-tracks', { params: { upc: album.upc } })
      .then(function (response) {
        var tracks = response.data.tracks || [];
        var items = tracks.map(function (t) {
          return {
            postId: null,
            title: t.title,
            artist: data.artist,
            artworkURL: album.artworkURL,
            catalogID: t.catalogID || null,
            previewURL: t.previewURL || null,
          };
        });
        if (items.length > 0) playback.playNow(items);
      })
      .catch(function () {});
  }

  function handleSongPlay(song) {
    if (!song.isrc) return;

    // If this song is already playing, toggle pause/play
    if (
      playback.currentTrack &&
      playback.currentTrack.artist === data.artist &&
      playback.currentTrack.title === song.songTitle
    ) {
      playback.togglePlayPause();
      return;
    }

    api
      .get('/api/music/preview', { params: { isrc: song.isrc } })
      .then(function (response) {
        var items = [
          {
            postId: null,
            title: song.songTitle,
            artist: data.artist,
            artworkURL: song.artworkURL,
            catalogID: response.data.catalogID || null,
            previewURL: response.data.previewURL || null,
          },
        ];
        playback.playNow(items);
      })
      .catch(function () {});
  }

  if (loading) return <LoadingText>Loading...</LoadingText>;
  if (error || !data) return <EmptyText>Artist not found.</EmptyText>;

  // Merge DB albums with Apple Music catalog albums
  var mergedAlbums = [];
  var seenAlbumTitles = {};

  // DB albums first (they have post counts)
  data.albums.forEach(function (a) {
    var key = (a.albumTitle || '').toLowerCase();
    seenAlbumTitles[key] = true;
    mergedAlbums.push(a);
  });

  // Add catalog-only albums (not already in DB)
  if (catalog) {
    catalog.forEach(function (c) {
      var key = (c.albumTitle || '').toLowerCase();
      if (!seenAlbumTitles[key]) {
        seenAlbumTitles[key] = true;
        mergedAlbums.push({
          id: 'catalog-' + c.albumTitle,
          albumTitle: c.albumTitle,
          artworkURL: c.artworkURL,
          releaseDate: c.releaseDate,
          genre: c.genre,
          upc: c.upc,
          catalogID: c.catalogID,
          postCount: 0,
        });
      }
    });
  }

  // Collect unique decades from all albums
  var allDecades = [];
  var decadeSet = {};
  mergedAlbums.forEach(function (item) {
    if (item.releaseDate) {
      var decade = item.releaseDate.slice(0, 3) + '0s';
      if (!decadeSet[decade]) {
        decadeSet[decade] = true;
        allDecades.push(decade);
      }
    }
  });
  allDecades.sort();

  // Filter albums by decade
  var filteredAlbums = mergedAlbums.filter(function (a) {
    if (decadeFilter) {
      var year = a.releaseDate ? a.releaseDate.slice(0, 4) : '';
      var decade = year.slice(0, 3) + '0s';
      if (decade !== decadeFilter) return false;
    }
    return true;
  });

  // Sort filtered albums
  var sortedAlbums = filteredAlbums.slice();
  if (albumSort === 'alpha-asc') {
    sortedAlbums.sort(function (a, b) {
      return (a.albumTitle || '').localeCompare(b.albumTitle || '');
    });
  } else if (albumSort === 'alpha-desc') {
    sortedAlbums.sort(function (a, b) {
      return (b.albumTitle || '').localeCompare(a.albumTitle || '');
    });
  } else if (albumSort === 'date-asc') {
    sortedAlbums.sort(function (a, b) {
      return (a.releaseDate || '').localeCompare(b.releaseDate || '');
    });
  } else {
    sortedAlbums.sort(function (a, b) {
      return (b.releaseDate || '').localeCompare(a.releaseDate || '');
    });
  }

  var hasCatalog = catalog != null;
  if (!hasCatalog && data.albums.length === 0 && data.songs.length === 0)
    return <EmptyText>No music found for this artist.</EmptyText>;

  var shareURL =
    window.location.origin +
    '/artist/' +
    encodeURIComponent(data.artist) +
    (appleId ? '?appleId=' + encodeURIComponent(appleId) : '');
  var shareText = 'Check out ' + data.artist + ' on HeavyRotation';

  var navFrom = location.state || {};
  var backLabel =
    navFrom.from === 'search'
      ? 'Back to Search'
      : navFrom.from === 'feed'
        ? 'Back to Feed'
        : navFrom.from === 'album'
          ? 'Back to Album'
          : 'Back to Search';
  var backPath = navFrom.fromPath || '/music/search';

  var artistPath =
    '/artist/' +
    encodeURIComponent(data.artist) +
    (appleId ? '?appleId=' + encodeURIComponent(appleId) : '');
  var forwardState = {
    from: 'artist',
    fromPath: artistPath,
    activeTab: activeTab,
    albumSort: albumSort,
    decadeFilter: decadeFilter,
  };

  return (
    <MusicContainer>
      <BackChevron to={backPath} state={navFrom}>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <polyline points="8 1 3 6 8 11" />
        </svg>
        {backLabel}
      </BackChevron>
      <MusicCard>
        <HeaderBanner>
          <ArtistName>{data.artist}</ArtistName>
        </HeaderBanner>
        {artistDetail && artistDetail.artworkURL && (
          <ArtistHero src={artistDetail.artworkURL} alt={data.artist} />
        )}
      </MusicCard>

      {showShare && (
        <ShareModal
          url={shareURL}
          text={shareText}
          shareType="artist"
          onClose={function () {
            setShowShare(false);
          }}
        />
      )}

      <TabBar>
        <Tab
          $active={activeTab === 'posts'}
          onClick={function () {
            setActiveTab('posts');
          }}
        >
          Posts
        </Tab>
        {data && data.mentionedInPosts && data.mentionedInPosts.length > 0 && (
          <Tab
            $active={activeTab === 'mentions'}
            onClick={function () {
              setActiveTab('mentions');
            }}
          >
            Mentioned in
          </Tab>
        )}
        <Tab
          $active={activeTab === 'albums'}
          onClick={function () {
            setActiveTab('albums');
          }}
        >
          Albums
        </Tab>
        {artistDetail && artistDetail.relatedArtists && artistDetail.relatedArtists.length > 0 && (
          <Tab
            $active={activeTab === 'related'}
            onClick={function () {
              setActiveTab('related');
            }}
          >
            Related
          </Tab>
        )}
      </TabBar>

      <CommunitySection style={{ display: activeTab === 'posts' ? 'flex' : 'none' }}>
        {communityPosts.length > 0 ? (
          <>
            {communityPosts
              .slice()
              .sort(function (a, b) {
                var aHasText = a.text && a.text.trim() ? 1 : 0;
                var bHasText = b.text && b.text.trim() ? 1 : 0;
                return bHasText - aHasText;
              })
              .map(function (post) {
                return <CompactPostCard key={post.id} post={post} />;
              })}
            {communityHasMore && (
              <LoadMoreButton onClick={loadMoreCommunityPosts} disabled={communityLoading}>
                {communityLoading ? 'Loading...' : 'Load more'}
              </LoadMoreButton>
            )}
          </>
        ) : (
          !communityLoading && (
            <EmptyText style={{ marginTop: '0.5rem' }}>
              Be the first to post about {data.artist}.
            </EmptyText>
          )
        )}
      </CommunitySection>

      <CommunitySection style={{ display: activeTab === 'mentions' ? 'flex' : 'none' }}>
        {data && data.mentionedInPosts && data.mentionedInPosts.length > 0 ? (
          data.mentionedInPosts.map(function (post) {
            return <CompactPostCard key={post.id} post={post} />;
          })
        ) : (
          <EmptyText style={{ marginTop: '0.5rem' }}>No mentions yet.</EmptyText>
        )}
      </CommunitySection>

      <div style={{ display: activeTab === 'albums' ? 'contents' : 'none' }}>
        {mergedAlbums.length > 0 && (
          <>
            <SectionHeader>
              <SectionHeading style={{ marginBottom: 0 }}>
                {mergedAlbums.length} {mergedAlbums.length === 1 ? 'Album' : 'Albums'}
              </SectionHeading>
              <SortGroup>
                <SortToggle
                  $active={albumSort.startsWith('date')}
                  onClick={function () {
                    setAlbumSort(albumSort === 'date-desc' ? 'date-asc' : 'date-desc');
                  }}
                >
                  Date{' '}
                  {albumSort === 'date-asc' ? '\u2191' : albumSort === 'date-desc' ? '\u2193' : ''}
                </SortToggle>
                <SortToggle
                  $active={albumSort.startsWith('alpha')}
                  onClick={function () {
                    setAlbumSort(albumSort === 'alpha-asc' ? 'alpha-desc' : 'alpha-asc');
                  }}
                >
                  A-Z{' '}
                  {albumSort === 'alpha-desc'
                    ? '\u2193'
                    : albumSort === 'alpha-asc'
                      ? '\u2191'
                      : ''}
                </SortToggle>
              </SortGroup>
            </SectionHeader>
            {allDecades.length > 1 && (
              <FilterRow>
                {allDecades.map(function (d) {
                  return (
                    <FilterChip
                      key={d}
                      $active={decadeFilter === d}
                      onClick={function () {
                        setDecadeFilter(decadeFilter === d ? null : d);
                      }}
                    >
                      {d}
                    </FilterChip>
                  );
                })}
              </FilterRow>
            )}
            <AlbumGrid>
              {sortedAlbums.map(function (album) {
                var year = album.releaseDate ? album.releaseDate.slice(0, 4) : null;
                return (
                  <AlbumCard
                    key={album.id}
                    to={
                      '/music/album?artist=' +
                      encodeURIComponent(data.artist) +
                      '&album=' +
                      encodeURIComponent(album.albumTitle) +
                      (album.catalogID ? '&catalogID=' + encodeURIComponent(album.catalogID) : '') +
                      (album.upc ? '&upc=' + encodeURIComponent(album.upc) : '')
                    }
                    state={forwardState}
                  >
                    <AlbumBanner>
                      <AlbumText>
                        <div style={{ fontWeight: 900 }}>{data.artist}</div>
                        <AlbumTitle>{album.albumTitle}</AlbumTitle>
                        <AlbumMeta>
                          {year && year + ' \u00B7 '}
                          {album.postCount > 0
                            ? album.postCount + ' ' + (album.postCount === 1 ? 'post' : 'posts')
                            : ''}
                        </AlbumMeta>
                      </AlbumText>
                      {album.upc && (
                        <AlbumPlayButton
                          onClick={function (e) {
                            e.preventDefault();
                            handleAlbumPlay(album, e);
                          }}
                        >
                          <PlayIcon width="16" height="16" />
                        </AlbumPlayButton>
                      )}
                    </AlbumBanner>
                    <AlbumArtwork src={album.artworkURL} alt={album.albumTitle} />
                  </AlbumCard>
                );
              })}
            </AlbumGrid>
          </>
        )}

        {data.songs.length > 0 && (
          <>
            <SectionHeading>Songs</SectionHeading>
            {data.songs.map(function (song) {
              var isPlaying = false;
              if (playback.currentTrack) {
                isPlaying =
                  playback.currentTrack.artist === data.artist &&
                  playback.currentTrack.title === song.songTitle &&
                  playback.playbackState === 'playing';
              }
              return (
                <SongRow key={song.id}>
                  <SongArtwork src={song.artworkURL} alt={song.songTitle} />
                  <SongText to={'/music/' + song.id}>
                    <SongTitle>{song.songTitle}</SongTitle>
                    <SongMeta>
                      {song.albumTitle && song.albumTitle + ' \u00B7 '}
                      {song.postCount} {song.postCount === 1 ? 'post' : 'posts'}
                    </SongMeta>
                  </SongText>
                  {song.isrc && (
                    <SongPlayButton
                      onClick={function () {
                        handleSongPlay(song);
                      }}
                    >
                      {isPlaying ? (
                        <PauseIcon width="14" height="14" />
                      ) : (
                        <PlayIcon width="14" height="14" />
                      )}
                    </SongPlayButton>
                  )}
                </SongRow>
              );
            })}
          </>
        )}
      </div>

      {activeTab === 'related' && artistDetail && artistDetail.relatedArtists && (
        <RelatedGrid>
          {artistDetail.relatedArtists.map(function (ra) {
            return (
              <RelatedCard
                key={ra.name}
                to={
                  '/artist/' +
                  encodeURIComponent(ra.name) +
                  (ra.appleId ? '?appleId=' + encodeURIComponent(ra.appleId) : '')
                }
              >
                <RelatedBanner>
                  <RelatedName>{ra.name}</RelatedName>
                </RelatedBanner>
                {ra.artworkURL ? (
                  <RelatedArtwork src={ra.artworkURL} alt={ra.name} />
                ) : (
                  <RelatedArtwork as="div" style={{ background: '#eee' }} />
                )}
              </RelatedCard>
            );
          })}
        </RelatedGrid>
      )}
    </MusicContainer>
  );
}
